import { Suspense, lazy as reactLazy, ComponentType } from "react";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { RoleGuard } from "@/components/guards/RoleGuard";
import { OrgPermissionGuard } from "@/components/guards/OrgPermissionGuard";

// Auto-recover from stale chunks after a redeploy:
// retry once, then force a hard reload (only once per session) instead of
// showing a blank screen.
const lazy = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) =>
  reactLazy(async () => {
    try {
      return await factory();
    } catch (err: any) {
      const msg = String(err?.message || err);
      const isChunkError =
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("error loading dynamically imported module");
      if (isChunkError && typeof window !== "undefined") {
        const key = "__chunk_reload__";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          // Return a never-resolving promise while reload happens
          return await new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    }
  });

// Outsourcing is the homepage — eagerly loaded
import Index from "./pages/Index";

// Lazy-loaded public pages
const CRMLanding = lazy(() => import("./pages/CRMLanding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const PricingPage = lazy(() => import("./pages/Pricing"));
const Signup = lazy(() => import("./pages/Signup"));
const SignupSuccess = lazy(() => import("./pages/SignupSuccess"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const SelectDestination = lazy(() => import("./pages/SelectDestination"));
const Clients = lazy(() => import("./pages/Clients"));
const ProposalAccept = lazy(() => import("./pages/ProposalAccept"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));

// Site Admin
const SiteAdmin = lazy(() => import("./pages/SiteAdmin"));
const SiteDashboard = lazy(() => import("./pages/site/Dashboard"));
const SitePosts = lazy(() => import("./pages/site/Posts"));
const SitePostEditor = lazy(() => import("./pages/site/PostEditor"));
const SiteCategories = lazy(() => import("./pages/site/Categories"));
const SiteTags = lazy(() => import("./pages/site/Tags"));
const SiteNewsletter = lazy(() => import("./pages/site/Newsletter"));
const SiteAnalytics = lazy(() => import("./pages/site/Analytics"));
const SiteIntegrations = lazy(() => import("./pages/site/Integrations"));
const SiteMediaLibrary = lazy(() => import("./pages/site/MediaLibrary"));
const SitePages = lazy(() => import("./pages/site/Pages"));
const SiteSEO = lazy(() => import("./pages/site/SEO"));
const SiteLeads = lazy(() => import("./pages/site/Leads"));
const SiteABTests = lazy(() => import("./pages/site/ABTests"));
const SiteAIAssistant = lazy(() => import("./pages/site/AIAssistant"));

// Platform Admin
const PlatformAdmin = lazy(() => import("./pages/PlatformAdmin"));
const PlatformDashboard = lazy(() => import("./pages/platform/Dashboard"));
const PlatformSuccessCenter = lazy(() => import("./pages/platform/SuccessCenter"));
const PlatformSupportCenter = lazy(() => import("./pages/platform/SupportCenter"));
const PlatformEmailTemplates = lazy(() => import("./pages/platform/EmailTemplates"));
const PlatformClients = lazy(() => import("./pages/platform/Clients"));
const PlatformInvoices = lazy(() => import("./pages/platform/Invoices"));
const PlatformHealthScores = lazy(() => import("./pages/platform/HealthScores"));
const PlatformChurnAnalysis = lazy(() => import("./pages/platform/ChurnAnalysis"));
const PlatformSaaSMetrics = lazy(() => import("./pages/platform/SaaSMetrics"));
const PlatformCoupons = lazy(() => import("./pages/platform/Coupons"));
const PlatformUsers = lazy(() => import("./pages/platform/Users"));
const PlatformPricing = lazy(() => import("./pages/platform/Pricing"));
const PlatformPartners = lazy(() => import("./pages/platform/Partners"));
const PlatformCommissions = lazy(() => import("./pages/platform/Commissions"));
const PlatformAuditLogs = lazy(() => import("./pages/platform/AuditLogs"));
const PlatformNPSDashboard = lazy(() => import("./pages/platform/NPSDashboard"));
const PlatformTrialLeads = lazy(() => import("./pages/platform/TrialLeads"));
const PlatformWhatsAppVerify = lazy(() => import("./pages/platform/WhatsAppVerify"));

// CRM
const CRMLayout = lazy(() => import("./pages/crm/CRMLayout"));
const CRMDashboard = lazy(() => import("./pages/crm/Dashboard"));
const Pipeline = lazy(() => import("./pages/crm/Pipeline"));
const Contacts = lazy(() => import("./pages/crm/Contacts"));
const ContactDetail = lazy(() => import("./pages/crm/ContactDetail"));
const Companies = lazy(() => import("./pages/crm/Companies"));
const CompanyForm = lazy(() => import("./pages/crm/CompanyForm"));
const CompanyDetail = lazy(() => import("./pages/crm/CompanyDetail"));
const Opportunities = lazy(() => import("./pages/crm/Opportunities"));
const Products = lazy(() => import("./pages/crm/Products"));
const OpportunityForm = lazy(() => import("./pages/crm/OpportunityForm"));
const OpportunityDetail = lazy(() => import("./pages/crm/OpportunityDetail"));
const Tasks = lazy(() => import("./pages/crm/Tasks"));
const TaskTemplates = lazy(() => import("./pages/crm/TaskTemplates"));
const WebForms = lazy(() => import("./pages/crm/WebForms"));
const Reports = lazy(() => import("./pages/crm/Reports"));
const CRMCalendar = lazy(() => import("./pages/crm/Calendar"));
const Automations = lazy(() => import("./pages/crm/Automations"));
const Teams = lazy(() => import("./pages/crm/Teams"));
const LossReasons = lazy(() => import("./pages/crm/LossReasons"));
const Goals = lazy(() => import("./pages/crm/Goals"));
const CRMUsers = lazy(() => import("./pages/crm/Users"));
const Inbox = lazy(() => import("./pages/crm/Inbox"));
const InboxLive = lazy(() => import("./pages/crm/InboxLive"));
const WhatsAppSettings = lazy(() => import("./pages/crm/WhatsAppSettings"));
const NumberHealth = lazy(() => import("./pages/crm/NumberHealth"));
const Scripts = lazy(() => import("./pages/crm/Scripts"));
const ScriptEditor = lazy(() => import("./pages/crm/ScriptEditor"));
const CRMSettings = lazy(() => import("./pages/crm/Settings"));
const Support = lazy(() => import("./pages/crm/Support"));
const SupportTicketDetail = lazy(() => import("./pages/crm/SupportTicketDetail"));
const CRMIntegrations = lazy(() => import("./pages/crm/Integrations"));
const AIAssistant = lazy(() => import("./pages/crm/AIAssistant"));
const QuickReplies = lazy(() => import("./pages/crm/QuickReplies"));
const Cadences = lazy(() => import("./pages/crm/Cadences"));
const CadenceDetail = lazy(() => import("./pages/crm/CadenceDetail"));
const MarketingCampaigns = lazy(() => import("./pages/crm/marketing/Campaigns"));
const MarketingCampaignForm = lazy(() => import("./pages/crm/marketing/CampaignForm"));
const MarketingTemplates = lazy(() => import("./pages/crm/marketing/Templates"));
const MarketingSegments = lazy(() => import("./pages/crm/marketing/Segments"));
const MarketingSegmentForm = lazy(() => import("./pages/crm/marketing/SegmentForm"));
const WhatsAppBudget = lazy(() => import("./pages/crm/marketing/WhatsAppBudget"));
const LeadScoring = lazy(() => import("./pages/crm/marketing/LeadScoring"));
const MarketingFlows = lazy(() => import("./pages/crm/marketing/Flows"));
const MarketingFlowForm = lazy(() => import("./pages/crm/marketing/FlowForm"));
const MarketingTags = lazy(() => import("./pages/crm/marketing/Tags"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min — reduce backend calls
      gcTime: 30 * 60 * 1000,   // 30 min — keep cache longer in memory
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: 1,                    // reduce retries on failure
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <OrganizationProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
          <Routes>
            {/* Outsourcing-first: homepage is the outsourcing landing page */}
            <Route path="/" element={<Index />} />
            <Route path="/outsourcing" element={<Index />} />
            <Route path="/crm-landing" element={<CRMLanding />} />
            <Route path="/checkout" element={<Navigate to="/pricing" replace />} />

            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup/success" element={<SignupSuccess />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/select-destination" element={<SelectDestination />} />
            <Route path="/clientes" element={<Clients />} />
            <Route path="/proposal/accept" element={<ProposalAccept />} />
            <Route path="/ajuda" element={<HelpCenter />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/login" element={<Navigate to="/admin/login" replace />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pending-approval" element={<PendingApproval />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            
            {/* Legacy /admin redirects */}
            <Route path="/admin" element={<Navigate to="/site" replace />} />
            <Route path="/admin/posts" element={<Navigate to="/site/posts" replace />} />
            <Route path="/admin/posts/:id" element={<Navigate to="/site/posts/:id" replace />} />
            <Route path="/admin/categories" element={<Navigate to="/site/categories" replace />} />
            <Route path="/admin/tags" element={<Navigate to="/site/tags" replace />} />
            <Route path="/admin/newsletter" element={<Navigate to="/site/newsletter" replace />} />
            <Route path="/admin/analytics" element={<Navigate to="/site/analytics" replace />} />
            <Route path="/admin/integrations" element={<Navigate to="/site/integrations" replace />} />
            <Route path="/admin/clients" element={<Navigate to="/platform/clients" replace />} />
            <Route path="/admin/invoices" element={<Navigate to="/platform/invoices" replace />} />
            <Route path="/admin/health" element={<Navigate to="/platform/health" replace />} />
            <Route path="/admin/churn" element={<Navigate to="/platform/churn" replace />} />
            <Route path="/admin/saas" element={<Navigate to="/platform/saas" replace />} />
            <Route path="/admin/coupons" element={<Navigate to="/platform/coupons" replace />} />
            <Route path="/admin/users" element={<Navigate to="/platform/users" replace />} />

            {/* Site Admin Routes */}
            <Route path="/site" element={<SiteAdmin />}>
              <Route index element={<SiteDashboard />} />
              <Route path="posts" element={<SitePosts />} />
              <Route path="posts/:id" element={<SitePostEditor />} />
              <Route path="categories" element={<SiteCategories />} />
              <Route path="tags" element={<SiteTags />} />
              <Route path="newsletter" element={<SiteNewsletter />} />
              <Route path="media" element={<SiteMediaLibrary />} />
              <Route path="pages" element={<SitePages />} />
              <Route path="seo" element={<SiteSEO />} />
              <Route path="leads" element={<SiteLeads />} />
              <Route path="ab-tests" element={<SiteABTests />} />
              <Route path="ai" element={<SiteAIAssistant />} />
              <Route path="analytics" element={
                <RoleGuard allowedRoles={["admin", "analyst", "suporte"]}>
                  <SiteAnalytics />
                </RoleGuard>
              } />
              <Route path="integrations" element={
                <RoleGuard allowedRoles={["admin"]}>
                  <SiteIntegrations />
                </RoleGuard>
              } />
            </Route>

            {/* Platform Admin Routes */}
            <Route path="/platform" element={<PlatformAdmin />}>
              <Route index element={<PlatformDashboard />} />
              <Route path="support" element={<PlatformSupportCenter />} />
              <Route path="success" element={<PlatformSuccessCenter />} />
              <Route path="email-templates" element={<PlatformEmailTemplates />} />
              <Route path="clients" element={<PlatformClients />} />
              <Route path="pricing" element={<PlatformPricing />} />
              <Route path="invoices" element={<PlatformInvoices />} />
              <Route path="health" element={<PlatformHealthScores />} />
              <Route path="churn" element={<PlatformChurnAnalysis />} />
              <Route path="saas" element={<PlatformSaaSMetrics />} />
              <Route path="coupons" element={<PlatformCoupons />} />
              <Route path="users" element={<PlatformUsers />} />
              <Route path="partners" element={<PlatformPartners />} />
              <Route path="commissions" element={<PlatformCommissions />} />
              <Route path="audit" element={<PlatformAuditLogs />} />
              <Route path="nps" element={<PlatformNPSDashboard />} />
              <Route path="trial-leads" element={<PlatformTrialLeads />} />
              <Route path="whatsapp-verify" element={
                <RoleGuard allowedRoles={["admin"]}>
                  <PlatformWhatsAppVerify />
                </RoleGuard>
              } />
            </Route>

            {/* CRM Routes */}
            <Route path="/crm" element={<CRMLayout />}>
              <Route index element={<CRMDashboard />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="opportunities" element={<Opportunities />} />
              <Route path="opportunities/new" element={<OpportunityForm />} />
              <Route path="opportunities/:id" element={<OpportunityDetail />} />
              <Route path="opportunities/:id/edit" element={<OpportunityForm />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="contacts/:id" element={<ContactDetail />} />
              <Route path="products" element={<Products />} />
              <Route path="companies" element={<Companies />} />
              <Route path="companies/new" element={<CompanyForm />} />
              <Route path="companies/:id" element={<CompanyDetail />} />
              <Route path="companies/:id/edit" element={<CompanyForm />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="task-templates" element={<TaskTemplates />} />
              <Route path="web-forms" element={<WebForms />} />
              <Route path="calendar" element={<CRMCalendar />} />
              <Route path="automations" element={
                <OrgPermissionGuard permission="canManageAutomations">
                  <Automations />
                </OrgPermissionGuard>
              } />
              <Route path="teams" element={
                <OrgPermissionGuard permission="canManageOwnTeam">
                  <Teams />
                </OrgPermissionGuard>
              } />
              <Route path="loss-reasons" element={
                <OrgPermissionGuard permission="canManageSettings">
                  <LossReasons />
                </OrgPermissionGuard>
              } />
              <Route path="goals" element={<Goals />} />
              <Route path="users" element={
                <OrgPermissionGuard permission="canManageUsers">
                  <CRMUsers />
                </OrgPermissionGuard>
              } />
              <Route path="reports" element={
                <OrgPermissionGuard permission="canViewReports">
                  <Reports />
                </OrgPermissionGuard>
              } />
              <Route path="inbox" element={<Inbox />} />
              <Route path="inbox/live" element={<InboxLive />} />
              <Route path="settings/whatsapp" element={<WhatsAppSettings />} />
              <Route path="settings/whatsapp/health" element={<NumberHealth />} />
              <Route path="scripts" element={<Scripts />} />
              <Route path="scripts/new" element={<ScriptEditor />} />
              <Route path="scripts/:id" element={<ScriptEditor />} />
              <Route path="quick-replies" element={<QuickReplies />} />
              <Route path="cadences" element={<Cadences />} />
              <Route path="cadences/:id" element={<CadenceDetail />} />
              {/* Support routes */}
              <Route path="support" element={<Support />} />
              <Route path="support/:id" element={<SupportTicketDetail />} />
              {/* Marketing routes */}
              <Route path="marketing/campaigns" element={<MarketingCampaigns />} />
              <Route path="marketing/campaigns/new" element={<MarketingCampaignForm />} />
              <Route path="marketing/campaigns/:id" element={<MarketingCampaignForm />} />
              <Route path="marketing/campaigns/:id/edit" element={<MarketingCampaignForm />} />
              <Route path="marketing/templates" element={<MarketingTemplates />} />
              <Route path="marketing/segments" element={<MarketingSegments />} />
              <Route path="marketing/segments/new" element={<MarketingSegmentForm />} />
              <Route path="marketing/segments/:id" element={<MarketingSegmentForm />} />
              <Route path="marketing/lead-scoring" element={<LeadScoring />} />
              <Route path="marketing/budget" element={<WhatsAppBudget />} />
              <Route path="marketing/flows" element={<MarketingFlows />} />
              <Route path="marketing/flows/new" element={<MarketingFlowForm />} />
              <Route path="marketing/flows/:id/edit" element={<MarketingFlowForm />} />
              <Route path="marketing/tags" element={<MarketingTags />} />
              <Route path="billing" element={
                <OrgPermissionGuard permission="canManageSettings">
                  <CRMSettings />
                </OrgPermissionGuard>
              } />
              <Route path="settings" element={
                <OrgPermissionGuard permission="canManageSettings">
                  <CRMSettings />
                </OrgPermissionGuard>
              } />
              <Route path="integrations" element={
                <OrgPermissionGuard permission="canManageSettings">
                  <CRMIntegrations />
                </OrgPermissionGuard>
              } />
              <Route path="ai" element={<AIAssistant />} />
            </Route>
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        </TooltipProvider>
      </OrganizationProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
